/**
 * NAD+ Image Slider Section
 * Handles Swiper.js slider functionality with scroll animations
 */

// Import Swiper and required modules
import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';

// Configure Swiper to use modules
Swiper.use([Navigation, Pagination, Autoplay]);

interface SliderOptions {
  slidesPerView: number;
  spaceBetween: number;
  autoplay: boolean;
  autoplayDelay: number;
  showArrows: boolean;
  showPagination: boolean;
}

class NADImageSlider {
  private section: HTMLElement;
  private swiper: Swiper | null = null;
  private swiperContainer: HTMLElement | null;
  private options: SliderOptions;

  constructor(section: HTMLElement) {
    this.section = section;
    this.swiperContainer = this.section.querySelector('.nad-image-slider__swiper');
    this.options = this.getOptionsFromSection();
    this.init();
  }

  /**
   * Initialize the slider component
   */
  private init(): void {
    if (!this.swiperContainer) {
      console.warn('NAD Image Slider: Swiper container not found');
      return;
    }

    this.setupSwiper();
    this.setupScrollAnimation();
    this.setupImageLoading();
    console.log('NAD Image Slider initialized');
  }

  /**
   * Extract options from section data attributes or defaults
   */
  private getOptionsFromSection(): SliderOptions {
    const section = this.section;
    
    return {
      slidesPerView: parseInt(section.dataset['slidesPerView'] || '3'),
      spaceBetween: parseInt(section.dataset['spaceBetween'] || '15'),
      autoplay: section.dataset['autoplay'] === 'true',
      autoplayDelay: parseInt(section.dataset['autoplayDelay'] || '3000'),
      showArrows: section.dataset['showArrows'] !== 'false',
      showPagination: section.dataset['showPagination'] !== 'false'
    };
  }

  /**
   * Setup Swiper slider
   */
  private setupSwiper(): void {
    if (!this.swiperContainer) return;

    const swiperConfig: any = {
      slidesPerView: 1,
      spaceBetween: this.options.spaceBetween,
      loop: false,
      grabCursor: true,
      breakpoints: {
        576: {
          slidesPerView: 1,
          spaceBetween: this.options.spaceBetween
        },
        768: {
          slidesPerView: 2,
          spaceBetween: this.options.spaceBetween
        },
        992: {
          slidesPerView: this.options.slidesPerView,
          spaceBetween: this.options.spaceBetween,
          loop: true
        }
      }
    };

    // Add navigation if enabled
    if (this.options.showArrows) {
      swiperConfig.navigation = {
        nextEl: '.nad-image-slider__nav--next',
        prevEl: '.nad-image-slider__nav--prev',
      };
    }

    // Add pagination if enabled
    if (this.options.showPagination) {
      swiperConfig.pagination = {
        el: '.nad-image-slider__pagination',
        clickable: true,
      };
    }

    // Add autoplay if enabled
    if (this.options.autoplay) {
      swiperConfig.autoplay = {
        delay: this.options.autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      };
    }

    // Initialize Swiper
    try {
      this.swiper = new Swiper(this.swiperContainer, swiperConfig);
      
      // Store reference for cleanup
      (this.section as any).__swiperInstance = this.swiper;
    } catch (error) {
      console.error('NAD Image Slider: Failed to initialize Swiper', error);
    }
  }

  /**
   * Setup scroll animation using Intersection Observer
   * Optimized for performance with reduced observers
   */
  private setupScrollAnimation(): void {
    if (!this.section) return;

    // Use a single observer for better performance
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Animate main section first
            if (entry.target === this.section) {
              entry.target.classList.add('animate-in');
              observer.unobserve(entry.target);
              
              // Then animate slides with a small delay
              setTimeout(() => {
                const slides = this.section.querySelectorAll('.nad-image-slider__slide');
                slides.forEach((slide, index) => {
                  setTimeout(() => {
                    slide.classList.add('animate-in');
                  }, index * 100); // Stagger by 100ms
                });
              }, 200);
            }
            // Animate individual slides if they come into view separately
            else if (entry.target.classList.contains('nad-image-slider__slide')) {
              entry.target.classList.add('animate-in');
              observer.unobserve(entry.target);
            }
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    // Observe main section
    observer.observe(this.section);

    // Store observer for cleanup
    (this.section as any).scrollObserver = observer;
  }

  /**
   * Setup lazy loading for images
   */
  private setupImageLoading(): void {
    const images = this.section.querySelectorAll<HTMLImageElement>('.nad-image-slider__image');
    
    images.forEach((img) => {
      // Set loading state
      img.dataset['loading'] = 'true';
      
      // Handle load event
      img.addEventListener('load', () => {
        img.dataset['loading'] = 'false';
      });

      // Handle error event
      img.addEventListener('error', () => {
        img.dataset['loading'] = 'false';
        console.warn('NAD Image Slider: Failed to load image', img.src);
      });

      // If image is already loaded (cached)
      if (img.complete) {
        img.dataset['loading'] = 'false';
      }
    });
  }

  /**
   * Cleanup method to destroy Swiper and observers
   */
  public destroy(): void {
    // Destroy Swiper instance
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    // Disconnect observer
    if (this.section) {
      if ((this.section as any).scrollObserver) {
        (this.section as any).scrollObserver.disconnect();
        delete (this.section as any).scrollObserver;
      }

      // Remove Swiper reference
      if ((this.section as any).__swiperInstance) {
        delete (this.section as any).__swiperInstance;
      }
    }
  }

  /**
   * Public method to update slider (useful for dynamic content)
   */
  public update(): void {
    if (this.swiper) {
      this.swiper.update();
    }
  }

  /**
   * Static method to initialize all slider sections
   */
  public static initializeAll(): NADImageSlider[] {
    const sections = document.querySelectorAll<HTMLElement>('.nad-image-slider');
    return Array.from(sections).map(section => {
      const instance = new NADImageSlider(section);
      // Store instance reference for external access
      (section as any).__nadImageSlider = instance;
      return instance;
    });
  }

  /**
   * Static method to destroy a specific slider instance
   */
  public static destroySection(section: HTMLElement): void {
    const instance = (section as any).__nadImageSlider;
    if (instance && instance.destroy) {
      instance.destroy();
      delete (section as any).__nadImageSlider;
    }
  }

  /**
   * Static method to get instance from section element
   */
  public static getInstance(section: HTMLElement): NADImageSlider | null {
    return (section as any).__nadImageSlider || null;
  }
}

// Export for use in other modules
export { NADImageSlider };

// Auto-initialize when DOM is ready (for sections loaded initially)
document.addEventListener('DOMContentLoaded', () => {
  NADImageSlider.initializeAll();
});

// Handle dynamically loaded sections (for Shopify section settings)
document.addEventListener('shopify:section:load', (event: Event) => {
  const target = (event as CustomEvent).detail.sectionId;
  const section = document.querySelector(`[data-section-id="${target}"]`);
  
  if (section && section.classList.contains('nad-image-slider')) {
    NADImageSlider.destroySection(section as HTMLElement);
    new NADImageSlider(section as HTMLElement);
  }
});

// Handle section unload
document.addEventListener('shopify:section:unload', (event: Event) => {
  const target = (event as CustomEvent).detail.sectionId;
  const section = document.querySelector(`[data-section-id="${target}"]`);
  
  if (section && section.classList.contains('nad-image-slider')) {
    NADImageSlider.destroySection(section as HTMLElement);
  }
});

export default NADImageSlider;
